import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  Plus,
  Search,
  Filter,
  DollarSign,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Download,
  MoreVertical,
  AlertCircle,
  Building2
} from 'lucide-react';
import { toast } from 'react-toastify';

import { fetchPaymentRequests, PaymentRequest } from '../../services/PaymentService';

const PaymentRequests: React.FC = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [paymentRequests, setPaymentRequests] = useState<PaymentRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [error, setError] = useState('');

  useEffect(() => {
    loadPaymentRequests();
  }, [statusFilter]);

  const loadPaymentRequests = async () => {
    try {
      setLoading(true);
      const status = statusFilter !== 'all' ? statusFilter : undefined;
      const requests = await fetchPaymentRequests(status);
      setPaymentRequests(requests);
      setError('');
    } catch (error) {
      setError('Failed to load payment requests');
      toast.error('Failed to load payment requests');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    if (theme === 'dark') {
      switch (status) {
        case 'pending': return 'text-yellow-300 bg-yellow-900/30 border-yellow-800';
        case 'paid': return 'text-green-300 bg-green-900/30 border-green-800';
        case 'overdue': return 'text-red-300 bg-red-900/30 border-red-800';
        case 'cancelled': return 'text-gray-300 bg-gray-800 border-gray-700';
        default: return 'text-gray-300 bg-gray-800 border-gray-700';
      }
    } else {
      switch (status) {
        case 'pending': return 'text-yellow-700 bg-yellow-100 border-yellow-200';
        case 'paid': return 'text-green-700 bg-green-100 border-green-200';
        case 'overdue': return 'text-red-700 bg-red-100 border-red-200';
        case 'cancelled': return 'text-gray-700 bg-gray-100 border-gray-200';
        default: return 'text-gray-700 bg-gray-100 border-gray-200';
      }
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'paid': return <CheckCircle className="w-4 h-4" />;
      case 'overdue': return <AlertCircle className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRelativeTime = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const getDaysUntilDue = (dueDate: string) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffMs = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    return `Due in ${diffDays} days`;
  };

  const filteredRequests = paymentRequests.filter(request => {
    // Safely check if properties exist before using toLowerCase()
    const projectTitle = typeof request.projectId === 'object' && request.projectId !== null && 'title' in request.projectId 
      ? request.projectId.title 
      : request.projectTitle || '';
      
    const description = request.description || '';
    
    const clientName = typeof request.clientId === 'object' && request.clientId !== null && 'firstName' in request.clientId && 'lastName' in request.clientId
      ? `${request.clientId.firstName} ${request.clientId.lastName}`
      : request.clientName || '';
    
    const matchesSearch = 
      projectTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      clientName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className={`h-16 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'} rounded-lg animate-pulse`}></div>
        <div className="space-y-4">
          {[...Array(5)].map((_, index) => (
            <div key={index} className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-6 rounded-lg shadow-sm border animate-pulse`}>
              <div className={`h-4 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded w-3/4 mb-4`}></div>
              <div className={`h-3 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded w-full mb-2`}></div>
              <div className={`h-3 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded w-2/3`}></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Payment Requests
          </h1>
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
            Manage and track client payment requests
          </p>
        </div>
        <Link
          to="/dashboard/admin/payment-requests/create"
          className={`flex items-center px-4 py-2 ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-lg transition-colors duration-200`}
        >
          <Plus className="w-4 h-4 mr-2" />
          New Request
        </Link>
      </div>

      {error && (
        <div className={`${
          theme === 'dark' ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'
        } border rounded-lg p-4`}>
          <div className="flex items-center">
            <AlertCircle className={`w-5 h-5 ${theme === 'dark' ? 'text-red-400' : 'text-red-500'} mr-2`} />
            <span className={`${theme === 'dark' ? 'text-red-300' : 'text-red-700'} text-sm`}>{error}</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-6 rounded-lg shadow-sm border`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} w-4 h-4`} />
              <input
                type="text"
                placeholder="Search payment requests..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-10 pr-4 py-2 w-full border ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              />
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`px-3 py-2 border ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <button className={`flex items-center px-4 py-2 ${
              theme === 'dark'
                ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            } rounded-lg transition-colors duration-200`}>
              <Download className="w-4 h-4 mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Payment Requests List */}
      {filteredRequests.length === 0 ? (
        <div className={`text-center py-12 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg shadow-sm border`}>
          <DollarSign className={`w-16 h-16 ${theme === 'dark' ? 'text-gray-700' : 'text-gray-300'} mx-auto mb-4`} />
          <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>No payment requests found</h3>
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-6`}>
            {paymentRequests.length === 0 
              ? "Create your first payment request to get started."
              : "Try adjusting your search or filter criteria."
            }
          </p>
          <Link
            to="/dashboard/admin/payment-requests/create"
            className={`inline-flex items-center px-4 py-2 ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-lg transition-colors duration-200`}
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Payment Request
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((request) => (
            <div
              key={request._id}
              className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700 hover:bg-gray-750' : 'bg-white border-gray-200 hover:bg-gray-50'} rounded-lg shadow-sm border hover:shadow-md transition-all duration-200`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <Link
                        to={`/dashboard/admin/payment-requests/${request._id}`}
                        className={`text-lg font-semibold ${theme === 'dark' ? 'text-white hover:text-blue-400' : 'text-gray-900 hover:text-blue-600'} truncate`}
                      >
                        {request.description}
                      </Link>
                      <span
                        className={`px-3 py-1 text-sm font-medium rounded-full capitalize flex items-center space-x-1 ${getStatusColor(request.status)}`}
                      >
                        {getStatusIcon(request.status)}
                        <span className="ml-1">{request.status}</span>
                      </span>
                    </div>
                    
                    <div className={`flex items-center space-x-6 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      <div className="flex items-center">
                        <Building2 className="w-4 h-4 mr-1" />
                        Client: {typeof request.clientId === 'object' && request.clientId !== null && 'firstName' in request.clientId
                          ? `${request.clientId.firstName} ${request.clientId.lastName}`
                          : request.clientName || 'Unknown Client'}
                      </div>
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {getDaysUntilDue(request.dueDate)}
                      </div>
                      <div>
                        Created: {getRelativeTime(request.createdAt)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <div className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {formatCurrency(request.amount, request.currency)}
                    </div>
                    <div className="flex items-center">
                      <Link
                        to={`/dashboard/admin/payment-requests/${request._id}`}
                        className={`p-2 ${theme === 'dark' ? 'text-gray-400 hover:text-blue-400 hover:bg-gray-700' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'} rounded-lg transition-colors duration-200`}
                        title="View details"
                      >
                        <Eye className="w-4 h-4" />
                      </Link>
                      <button
                        className={`p-2 ${theme === 'dark' ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'} rounded-lg transition-colors duration-200`}
                        title="More options"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className={`mt-4 pt-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Project: {typeof request.projectId === 'object' && request.projectId !== null && 'title' in request.projectId
                        ? request.projectId.title
                        : request.projectTitle || 'Unknown Project'}
                    </div>
                    {request.status === 'paid' && request.paidAt && (
                      <div className={`text-sm ${theme === 'dark' ? 'text-green-400' : 'text-green-600'} flex items-center`}>
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Paid on {formatDate(request.paidAt)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PaymentRequests;